import { useState, MouseEvent } from 'react';
import { Box, IconButton, Typography, Menu, MenuItem } from '@mui/material';
import { ArrowBack, Close, DensityMedium } from '@mui/icons-material';
import { menuConstants } from '@constants/menuConstants';
import { PrivateRoutesTitles } from '@enums/privateRoutesTitles';

interface MenuItem {
  key: string;
  title: string;
  path?: string;
  icon?: boolean;
  children?: MenuItem[];
  parentKey?: string;
}

interface MobileMenuProps {
  onMenuClick: (path?: string) => void;
}

const MobileMenu = ({ onMenuClick }: MobileMenuProps) => {
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);
  const [currentMobileMenu, setCurrentMobileMenu] = useState<MenuItem | null>(
    null
  );

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorElMenu(null);
  };

  const handleMobileMenuClick = (item: MenuItem) => {
    if (item.children) {
      setCurrentMobileMenu(item);
    } else if (item.path) {
      handleCloseMenu();
      onMenuClick(item.path);
    }
  };

  const handleMobileMenuBack = () => {
    const parentMenu = menuConstants.find(
      item => item.key === currentMobileMenu?.parentKey
    );
    setCurrentMobileMenu(parentMenu || null);
  };

  return (
    <Box sx={{ display: { xs: 'flex', md: 'flex', lg: 'none' } }}>
      <IconButton aria-label="menu button" onClick={handleOpenMenu}>
        <DensityMedium />
      </IconButton>
      <Menu
        id="menu-appbar-density"
        anchorEl={anchorElMenu}
        open={Boolean(anchorElMenu)}
        onClose={handleCloseMenu}
        disableScrollLock
        keepMounted
        slotProps={{
          paper: {
            sx: {
              position: 'fixed',
              left: 0,
              right: 0,
              top: 'calc(64px + var(--announcement-offset, 0px))',
              height: 'calc(100vh - 64px - var(--announcement-offset, 0px))',
              maxHeight: 'calc(100vh - 64px - var(--announcement-offset, 0px))',
              overflowY: 'auto',
              width: { xs: '100%' },
              maxWidth: 'none',
              borderRadius: 0,
              padding: 1,
              margin: 0,
              marginLeft: 2,
              marginTop: 2,
            },
          },
          list: {
            sx: {
              padding: 0,
              height: 'auto',
              maxHeight: 'inherit',
              overflowY: 'auto',
            },
          },
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            bgcolor: 'white',
            zIndex: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
            }}
          >
            <Typography variant="h5">Menu</Typography>
            <IconButton
              onClick={handleCloseMenu}
              size="small"
              aria-label="Close menu"
              sx={{ color: 'gray' }}
            >
              <Close />
            </IconButton>
          </Box>
          {currentMobileMenu && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                pb: 2,
              }}
            >
              <IconButton
                onClick={handleMobileMenuBack}
                size="small"
                aria-label="Go back"
                sx={{ color: 'gray' }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                sx={{
                  color: 'gray',
                  fontWeight: 500,
                }}
              >
                {currentMobileMenu.title}
              </Typography>
            </Box>
          )}
        </Box>

        {currentMobileMenu
          ? currentMobileMenu.children?.map(item => (
              <MenuItem
                key={item.key}
                onClick={() => handleMobileMenuClick(item)}
                sx={{
                  py: 2,
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <Typography
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  {item.title}
                  {(item as MenuItem).children && (
                    <span style={{ fontSize: '1.2em' }}>›</span>
                  )}
                </Typography>
              </MenuItem>
            ))
          : menuConstants
              .filter(item => item.key !== PrivateRoutesTitles.MESSAGES)
              .map(item => (
                <MenuItem
                  key={item.key}
                  onClick={() => handleMobileMenuClick(item)}
                  sx={{
                    py: 2,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    {item.title}
                    {(item as MenuItem).children && (
                      <span style={{ fontSize: '1.2em' }}>›</span>
                    )}
                  </Typography>
                </MenuItem>
              ))}
      </Menu>
    </Box>
  );
};

export default MobileMenu;
